import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

import { BarSeriesType } from '@mui/x-charts';

import { Bucket } from '../../../../../api/indicators';
import { DashboardFiltersContext } from '../../filters/DashboardFiltersContext';
import { UserContext } from '../../../../../context/UserContext';

import { formatValue, getUniqueProperties } from '../../../../../helpers/helpers';
import { formatPeriodLabel } from '../../../../../helpers/dateUtils';

import DoubleBarChartBase from './DoubleBarChartBase';
import LineBarChartError from '../lineBarChart/LineBarChartError';
import LineBarChartSkeleton from '../lineBarChart/LineBarChartSkeleton';
import ChartNoData from '../ChartNoData';
import LabeledChip from '../LabeledChip';

export interface DoubleBarChartDataItem {
  dateTime: Date;
  volume: number;
  [key: string]: number | Date;
}

interface Props {
  title: string;
  currency?: string;
  description: string;
  textLink?: string;
  linkChart?: string;
  isLoading: boolean;
  isError: boolean;
  leftAxisLabel: string;
  rightAxisLabel: string;
  data: DoubleBarChartDataItem[];
  dataCompared: DoubleBarChartDataItem[];
  bucket: Bucket;
  setBucket: (bucket: Bucket) => void;
  height: number;
  currentDateFrom: string;
  currentDateTo: string;
  comparedDateFrom: string;
  comparedDateTo: string;
}

const selectStyle: CSSProperties = {
  fontFamily: 'Open Sans',
  fontWeight: 400,
  fontSize: '16px',
  width: '210px',
};

const DoubleBarChartCompared = ({
  title,
  currency,
  description,
  textLink,
  linkChart,
  isLoading,
  isError,
  leftAxisLabel,
  rightAxisLabel,
  data,
  dataCompared,
  bucket,
  setBucket,
  height,
  currentDateFrom,
  currentDateTo,
  comparedDateFrom,
  comparedDateTo,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { locale } = useContext(UserContext);
  const { openFilters } = useContext(DashboardFiltersContext);

  const colours = useMemo(
    () => [
      theme.palette.blue.main,
      theme.palette.cyan.main,
      theme.palette.teal.main,
      theme.palette.lightGreen.main,
      theme.palette.orange.main,
      theme.palette.deepOrange.main,
      theme.palette.purple.main,
    ],
    [theme]
  );

  const buildBarSeries = useCallback(
    (dataProperties: string[]): BarSeriesType[] => {
      const tenderSeries: BarSeriesType[] = dataProperties.map(
        (key, index) => ({
          id: key,
          label: key,
          type: 'bar',
          dataKey: key,
          stack: 'amount',
          yAxisId: 'y-axis-left-id',
          color: colours[index % colours.length],
          valueFormatter: v =>
            formatValue(v, true, currency, locale),
        })
      );

      const volumeSeries: BarSeriesType = {
        id: 'volume',
        label: t('screens.dashboarding.charts.volume'),
        type: 'bar',
        dataKey: 'volume',
        yAxisId: 'y-axis-right-id',
        color: theme.palette.darkerGrey.light,
      };

      return [...tenderSeries, volumeSeries];
    },
    [colours, currency, locale, theme, t]
  );

  const [series, setSeries] = useState<BarSeriesType[]>([]);
  const [seriesCompared, setSeriesCompared] = useState<BarSeriesType[]>([]);

  const noData = !data?.length;
  const noDataCompared = !dataCompared?.length;
  const noDataAtAll = noData && noDataCompared;

  const currentDateLabel = `${formatPeriodLabel(
    new Date(currentDateFrom),
    locale
  )} - ${formatPeriodLabel(new Date(currentDateTo), locale)}`;

  const comparedDateLabel = `${formatPeriodLabel(
    new Date(comparedDateFrom),
    locale
  )} - ${formatPeriodLabel(new Date(comparedDateTo), locale)}`;

  useEffect(() => {
    if (noData) return;

    const props = getUniqueProperties(data, ['dateTime', 'volume']);
    setSeries(buildBarSeries(props));
  }, [data, buildBarSeries]);

  useEffect(() => {
    if (noDataCompared) return;

    const props = getUniqueProperties(dataCompared, [
      'dateTime',
      'volume',
    ]);
    setSeriesCompared(buildBarSeries(props));
  }, [dataCompared, buildBarSeries]);

  const { sanitizedData, sanitizedComparedData } = useMemo<{
    sanitizedData: DoubleBarChartDataItem[];
    sanitizedComparedData: DoubleBarChartDataItem[];
  }>(() => {
    const sanitize = (
      items: DoubleBarChartDataItem[]
    ): DoubleBarChartDataItem[] =>
      items.map((item) => {
        const sanitizedItem = { ...item };

        Object.keys(sanitizedItem).forEach((key) => {
          const value = sanitizedItem[key as keyof DoubleBarChartDataItem];

          if (
            key !== 'dateTime' &&
            typeof value === 'number' &&
            value < 0
          ) {
            sanitizedItem[key as keyof DoubleBarChartDataItem] = 0 as never;
          }
        });

        return sanitizedItem;
      });

    return {
      sanitizedData: sanitize(data),
      sanitizedComparedData: sanitize(dataCompared),
    };
  }, [data, dataCompared]);

  return (
    <Box>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey['12p']}`,
          marginX: '8px',
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}
      >
        {isLoading && <LineBarChartSkeleton />}

        {!isLoading && (
          <>
            {isError && (
              <LineBarChartError
                title={title}
                description={description}
                bucket={bucket}
                setBucket={setBucket}
              />
            )}

            {!isError && noDataAtAll && (
              <ChartNoData
                height={height}
                title={title}
                description={description}
                openFilters={openFilters}
              />
            )}

            {!isError && !noDataAtAll && (
              <>
                <Box sx={{ display: 'flex', margin: '6px' }}>
                  <Box>
                    <Typography variant="h2">{title}</Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      {description}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <TextField
                      select
                      sx={selectStyle}
                      label={t('screens.dashboarding.charts.showBy')}
                      value={bucket}
                      onChange={e => setBucket(e.target.value as Bucket)}
                    >
                      <MenuItem value="hour">{t('components.select.hour')}</MenuItem>
                      <MenuItem value="day">{t('components.select.days')}</MenuItem>
                      <MenuItem value="week">{t('components.select.week')}</MenuItem>
                    </TextField>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', marginLeft: '20px' }}>
                  <Box sx={{ width: '50%', mr: 1 }}>
                    {noData ? (
                      <ChartNoData height={height} openFilters={openFilters} />
                    ) : (
                      <>
                        <LabeledChip
                          label={currentDateLabel}
                          color="primary.dark"
                          background="secondary.main"
                        />
                        <DoubleBarChartBase
                          bucket={bucket}
                          series={series}
                          data={sanitizedData}
                          height={height}
                          leftAxisLabel={leftAxisLabel}
                          rightAxisLabel={rightAxisLabel}
                          currency={currency}
                        />
                      </>
                    )}
                  </Box>

                  <Box sx={{ width: '50%', ml: 1 }}>
                    {noDataCompared ? (
                      <ChartNoData height={height} openFilters={openFilters} />
                    ) : (
                      <>
                        <LabeledChip
                          label={comparedDateLabel}
                          color={theme.palette.amber.dark}
                          // eslint-disable-next-line dot-notation
                          background={theme?.palette?.warning['190p'] || theme.palette.amber.light}
                        />
                        <DoubleBarChartBase
                          bucket={bucket}
                          series={seriesCompared}
                          data={sanitizedComparedData}
                          height={height}
                          leftAxisLabel={leftAxisLabel}
                          rightAxisLabel={rightAxisLabel}
                          currency={currency}
                        />
                      </>
                    )}
                  </Box>
                </Box>

                {!!linkChart && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
                    <Button
                      href={linkChart}
                      sx={{ fontSize: 16, fontWeight: 600 }}
                    >
                      {textLink}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Card>
    </Box>
  );
};

export default DoubleBarChartCompared;
